/**
 * Team data normalization functions
 *
 * Normalise raw API objects from the team-members endpoint into
 * consistent shapes used throughout the Team feature (Redux store,
 * components, modals, etc.).
 *
 * Follows the same pattern as signal-normalizers.js, vehicle-normalizers.js, etc.
 */

// ──────────────────────────────────────────────────────────────
// Type definitions
// ──────────────────────────────────────────────────────────────

/**
 * @typedef {Object} Jurisdiction
 * @property {string} id   - Short jurisdiction key, e.g. "sjc"
 * @property {string} name - Display name, e.g. "San Jose"
 */

/**
 * @typedef {Object} TeamUser
 * @property {number|string} id            - Unique identifier (userID)
 * @property {string|null}   username      - Username (may be null)
 * @property {string}        email         - User email
 * @property {string}        role          - Raw role string, e.g. "lyt_admin"
 * @property {Jurisdiction[]} jurisdictions - Agencies / jurisdictions the user belongs to
 * @property {string|null}   lastLoginAt   - ISO timestamp of last login
 * @property {string|null}   createdAt     - ISO timestamp of account creation
 * @property {string|null}   modifiedAt    - ISO timestamp of last modification
 * @property {boolean}       isPending     - Always false for confirmed users
 */

/**
 * @typedef {Object} TeamInvite
 * @property {string}      id            - Unique identifier (email or invitation ID)
 * @property {string}      email         - Invitee email address
 * @property {string}      role          - Assigned role for the invite
 * @property {string|null} invitationId  - Auth0 / backend invitation ID
 * @property {string|null} expiresAt     - ISO timestamp of invite expiry
 * @property {string|null} createdAt     - ISO timestamp of when the invite was sent
 * @property {boolean}     isPending     - Always true for invites
 */

// ──────────────────────────────────────────────────────────────
// Normalizers
// ──────────────────────────────────────────────────────────────

/**
 * Normalize a raw API user object into a consistent TeamUser shape.
 *
 * API shape (GET /users/agency/team-members):
 *   userID, username, email, role, jurisdictions[{id,name}],
 *   created_at_ts, modified_at_ts, last_login_at_ts
 *
 * @param {Object} raw - Raw user object from the API
 * @returns {TeamUser}
 */
export const normalizeTeamUser = (raw = {}) => ({
    id: raw.userID ?? raw.id ?? raw.email ?? raw.user_email ?? null,
    username: raw.username || null,
    email: raw.email || '',
    role: raw.role || '',
    jurisdictions: Array.isArray(raw.jurisdictions) ? raw.jurisdictions : [],
    lastLoginAt: raw.last_login_at_ts || raw.lastLoginAt || null,
    createdAt: raw.created_at_ts || raw.createdAt || null,
    modifiedAt: raw.modified_at_ts || raw.modifiedAt || null,
    isPending: false,
});

/**
 * Normalize a raw API invite object into a consistent TeamInvite shape.
 *
 * Invite objects arrive in the same mixed array as users but carry
 * `is_prospect: true`. Field names may vary between backends, so
 * the normalizer accounts for known aliases.
 *
 * @param {Object} raw - Raw invite object from the API
 * @returns {TeamInvite}
 */
export const normalizeTeamInvite = (raw = {}) => ({
    id: raw.inviteeEmail || raw.auth0InvitationID || raw.id || null,
    email: raw.inviteeEmail || raw.userEmail || raw.email || '',
    role: raw.assignedRole || raw.role || '',
    invitationId: raw.auth0InvitationID || raw.invitationId || null,
    expiresAt: raw.expiresAtTs || raw.expires_at || raw.expiresAt || null,
    createdAt: raw.created_at_ts || raw.createdAt || null,
    status: raw.status || null,
    jurisdictions: Array.isArray(raw.jurisdictions) ? raw.jurisdictions : [],
    isPending: true,
});

/**
 * Normalize a mixed array of raw API items (users + invites) into
 * consistent model objects. Items with `is_prospect: true` are treated
 * as invites; everything else is treated as a user.
 *
 * @param {Array} items - Raw items from the API response
 * @returns {Array<TeamUser|TeamInvite>}
 */
export const normalizeTeamMembers = (items = []) =>
    items.map((item) =>
        item.is_prospect ? normalizeTeamInvite(item) : normalizeTeamUser(item),
    );

/**
 * Normalize a raw invitation from the /invitations endpoint.
 *
 * The shape may differ from the inline invite objects returned
 * in the team-members mixed array, so we keep a dedicated normalizer.
 *
 * @param {Object} raw - Raw invitation object from the API
 * @returns {TeamInvite}
 */
export const normalizeInvitation = (raw = {}) => ({
    id: raw.inviteeEmail || raw.auth0InvitationID || raw.id || null,
    email: raw.inviteeEmail || raw.userEmail || raw.email || '',
    role: raw.assignedRole || raw.role || '',
    invitationId: raw.auth0InvitationID || raw.invitationId || null,
    expiresAt: raw.expiresAtTs || raw.expires_at || raw.expiresAt || null,
    createdAt: raw.created_at_ts || raw.createdAtTs || raw.createdAt || null,
    isPending: true,

    // Request-specific fields used by the Requests page
    requestType: raw.requestType || null,
    status: raw.status || null,
    accountApproved: raw.accountApproved || false,
    approvedByUserEmail: raw.approvedByUserEmail || null,
    approvedAt: raw.approvedAtTs || raw.approved_at_ts || raw.approvedAt || null,
    modifiedByUserEmail: raw.modifiedByUserEmail || null,
    agencyId: raw.agencyId || null,
    jurisdictions: Array.isArray(raw.jurisdictions) ? raw.jurisdictions : [],
    changeReason: raw.changeReason || null,

    // Requester / invitation lifecycle fields
    invitedByUserEmail: raw.invitedByUserEmail || null,
    sentAtTs: raw.sentAtTs || null,
    acceptedAtTs: raw.acceptedAtTs || null,
    sendInvitationEmail: raw.sendInvitationEmail ?? null,
    userEmail: raw.userEmail || null,
});

/**
 * Normalize an array of raw invitation objects from /invitations.
 *
 * @param {Array} items - Raw items from the API response
 * @returns {Array<TeamInvite>}
 */
export const normalizeInvitations = (items = []) =>
    items.map(normalizeInvitation);
